import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import ReactECharts from "echarts-for-react";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./Generale.css";
import "../../HrGenerale/TrendKpi/TrendKpi.css"; // riuso toggle M/T/A, fullscreen, download btn
import Lf23Loader from "../../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// ── Helpers di formattazione ──

const fmtEuro = (v) => {
    if (v == null) return "—";
    return v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " €";
};

const fmtPerc = (v) => {
    if (v == null) return "—";
    return v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
};

const fmtCompact = (v) => {
    if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toLocaleString("it-IT", { maximumFractionDigits: 1 }) + "Mln";
    if (Math.abs(v) >= 1_000) return (v / 1_000).toLocaleString("it-IT", { maximumFractionDigits: 1 }) + "K";
    return v.toLocaleString("it-IT", { maximumFractionDigits: 0 });
};

const fmtCompactEuro = (v) => fmtCompact(v) + " €";

// Helper: trimestre da numero mese (1-12)
const getTrimestre = (mese) => Math.ceil(mese / 3);

// Nomi mesi abbreviati italiani
const MESI_LABEL = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const MESI_FULL = ['', 'GENNAIO', 'FEBBRAIO', 'MARZO', 'APRILE', 'MAGGIO', 'GIUGNO', 'LUGLIO', 'AGOSTO', 'SETTEMBRE', 'OTTOBRE', 'NOVEMBRE', 'DICEMBRE'];

// ── Cross-filter helpers ──
// Restituisce i numeri dei mesi coinvolti nel filtro (1-12)
const getFilterMonths = (filter) => {
    if (filter.type === 'mese') return [filter.mese_num];
    if (filter.type === 'trimestre') {
        const t = filter.trimestre;
        return [(t - 1) * 3 + 1, (t - 1) * 3 + 2, (t - 1) * 3 + 3];
    }
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]; // anno
};

// Restituisce i numeri dei mesi dell'item aggregato, in base al viewType del grafico
const getItemMonths = (item, viewType) => {
    if (viewType === 'mese') return [item.mese_num];
    if (viewType === 'trimestre') {
        const t = item.trim || item.trimestre;
        return [(t - 1) * 3 + 1, (t - 1) * 3 + 2, (t - 1) * 3 + 3];
    }
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]; // anno
};

// Verifica se un item di un grafico (con un certo viewType) ha sovrapposizione col filtro attivo
const filterMatchesItem = (filter, item, viewType) => {
    if (!filter) return true;
    if (item.anno !== filter.anno) return false;
    const fMonths = getFilterMonths(filter);
    const iMonths = getItemMonths(item, viewType);
    return fMonths.some(m => iMonths.includes(m));
};

// ── Componente ──

const Generale = ({ filters }) => {
    const [loading, setLoading] = useState(false);
    const [kpiData, setKpiData] = useState([]);
    const [barData, setBarData] = useState([]);
    const [comboData, setComboData] = useState([]);
    const [lineData, setLineData] = useState([]);

    const [tableViewType, setTableViewType] = useState('anno');
    const [barViewType, setBarViewType] = useState('trimestre');
    const [comboViewType, setComboViewType] = useState('mese');
    const [lineViewType, setLineViewType] = useState('mese');
    const [expandedSettori, setExpandedSettori] = useState(new Set());
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);
    const [isFullscreenBar, setIsFullscreenBar] = useState(false);
    const [isFullscreenCombo, setIsFullscreenCombo] = useState(false);
    const [isFullscreenLine, setIsFullscreenLine] = useState(false);
    const [chartFilter, setChartFilter] = useState(null);
    // chartFilter = { type: 'mese'|'trimestre'|'anno', anno, mese_num?, trimestre?, source: 'bar'|'combo'|'line', viewType }
    const chartFilterRef = useRef(null);
    // Teniamo il ref sincronizzato con lo stato
    useEffect(() => { chartFilterRef.current = chartFilter; }, [chartFilter]);

    // Contatore per forzare re-mount dei grafici quando si esce dal fullscreen
    const [chartKey, setChartKey] = useState(0);
    const prevFullscreen = useRef({ isFullscreenCombo, isFullscreenBar, isFullscreenLine });
    useEffect(() => {
        const prev = prevFullscreen.current;
        // Se uno qualsiasi passa da true a false (uscita fullscreen), incrementa la key
        if ((prev.isFullscreenCombo && !isFullscreenCombo) ||
            (prev.isFullscreenBar && !isFullscreenBar) ||
            (prev.isFullscreenLine && !isFullscreenLine)) {
            setChartKey(k => k + 1);
        }
        prevFullscreen.current = { isFullscreenCombo, isFullscreenBar, isFullscreenLine };
    }, [isFullscreenCombo, isFullscreenBar, isFullscreenLine]);

    // ESC per uscire da fullscreen
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenTable(false);
                setIsFullscreenBar(false);
                setIsFullscreenCombo(false);
                setIsFullscreenLine(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // Clear chartFilter quando cambia il viewType del grafico sorgente
    // Usa il ref per evitare che chartFilter nelle dipendenze causi cicli di re-render
    useEffect(() => {
        const cf = chartFilterRef.current;
        if (!cf) return;
        if (cf.source === 'bar' && barViewType !== cf.viewType) {
            setChartFilter(null);
        } else if (cf.source === 'combo' && comboViewType !== cf.viewType) {
            setChartFilter(null);
        } else if (cf.source === 'line' && lineViewType !== cf.viewType) {
            setChartFilter(null);
        }
    }, [barViewType, comboViewType, lineViewType]);

    // ── Fetch dati ──
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setChartFilter(null); // Clear filtro quando cambiano i filtri genitore
            try {
                const params = {
                    fromDate: filters.fromDate || "",
                    toDate: filters.toDate || "",
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    tipContratto: filters.tipContratto?.map(t => t.value).join(",") || "",
                    commessa: filters.commessa?.map(c => c.value).join(",") || "",
                    livello1: filters.livello1?.map(l => l.value).join(",") || "",
                    livello2: filters.livello2?.map(l => l.value).join(",") || "",
                    livello3: filters.livello3?.map(l => l.value).join(",") || "",
                    livello4: filters.livello4?.map(l => l.value).join(",") || "",
                };
                const res = await axios.get(`${API_URL}/ambiente/generale`, { params });
                setKpiData(res.data.kpiData || []);
                setBarData(res.data.barData || []);
                setComboData(res.data.comboData || []);
                setLineData(res.data.lineData || []);
            } catch (error) {
                console.error("Errore nel caricamento dati Generale:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [filters]);

    const toggleSettore = (settore) => {
        setExpandedSettori(prev => {
            const next = new Set(prev);
            if (next.has(settore)) next.delete(settore);
            else next.add(settore);
            return next;
        });
    };

    // ════════════════════════════════════════════
    // Tabella KPI con periodi dinamici (M/T/A)
    // ════════════════════════════════════════════
    //
    // kpiData dal backend: array di { anno, mese_num, desc_settore, desc_commessa, tot_costo, tot_ore, tot_ricavo }
    // Ogni riga è un dato mensile per una specifica commessa.
    //
    // La tabella ha:
    //   - RIGHE = settori (espandibili) + commesse
    //   - COLONNE = periodi (in base a M/T/A) × 3 sub-colonne (Costo Orario, Costo%, Ricavo Orario) + Totale
    //
    // Logica:
    //   1. Raggruppo i dati per "chiave riga" (settore o settore+commessa)
    //   2. Per ogni chiave, raggruppo per periodo (mese/trimestre/anno)
    //   3. Per ogni periodo sommo tot_costo, tot_ore, tot_ricavo
    //   4. Calcolo i 3 KPI come rapporti delle somme

    const tableData = useMemo(() => {
        if (!kpiData || kpiData.length === 0) return { periods: [], settori: {} };

        // Trova tutti i periodi unici presenti nei dati
        const periodsSet = new Set();
        kpiData.forEach(r => {
            if (tableViewType === 'mese') {
                periodsSet.add(`${r.anno}-${r.mese_num}`);
            } else if (tableViewType === 'trimestre') {
                periodsSet.add(`${r.anno}-T${getTrimestre(r.mese_num)}`);
            } else {
                periodsSet.add(`${r.anno}`);
            }
        });

        // Ordina i periodi cronologicamente
        const periods = Array.from(periodsSet).sort((a, b) => {
            const partsA = a.split('-');
            const partsB = b.split('-');
            const annoA = parseInt(partsA[0]);
            const annoB = parseInt(partsB[0]);
            if (annoA !== annoB) return annoA - annoB;
            if (partsA.length === 1) return 0;
            const subA = partsA[1].startsWith('T') ? parseInt(partsA[1].slice(1)) : parseInt(partsA[1]);
            const subB = partsB[1].startsWith('T') ? parseInt(partsB[1].slice(1)) : parseInt(partsB[1]);
            return subA - subB;
        });

        // Label per ogni periodo
        const periodLabels = periods.map(p => {
            const parts = p.split('-');
            if (tableViewType === 'mese') return MESI_FULL[parseInt(parts[1])];
            if (tableViewType === 'trimestre') return parts[1]; // "T1", "T2"...
            return parts[0]; // anno
        });

        // Funzione: per una riga di dati, restituisce la chiave del periodo
        const getPeriodKey = (r) => {
            if (tableViewType === 'mese') return `${r.anno}-${r.mese_num}`;
            if (tableViewType === 'trimestre') return `${r.anno}-T${getTrimestre(r.mese_num)}`;
            return `${r.anno}`;
        };

        // Raggruppo anni per header multi-livello
        const anniMap = {};
        periods.forEach((p, i) => {
            const anno = p.split('-')[0];
            if (!anniMap[anno]) anniMap[anno] = [];
            anniMap[anno].push(i);
        });

        // Trim groups (vista mensile): raggruppa periodi per trimestre
        // Serve per inserire la colonna TOTALE dopo ogni trimestre nell'header e nei dati
        const trimGroups = [];
        if (tableViewType === 'mese') {
            const trimMap = {};
            periods.forEach((p, i) => {
                const [anno, meseStr] = p.split('-');
                const trim = getTrimestre(parseInt(meseStr));
                const key = `${anno}-T${trim}`;
                if (!trimMap[key]) {
                    trimMap[key] = { anno, trim, label: `TRIM ${trim}`, key, monthIndices: [] };
                }
                trimMap[key].monthIndices.push(i);
            });
            Object.keys(trimMap).sort().forEach(k => trimGroups.push(trimMap[k]));
        }

        // Raggruppo dati per settore e commessa
        // Struttura: { "03|Ambiente": { commesse: { "Costi Generali": [...rows], ... }, allRows: [...] } }
        const settori = {};
        kpiData.forEach(r => {
            if (!settori[r.desc_settore]) settori[r.desc_settore] = { commesse: {}, allRows: [] };
            settori[r.desc_settore].allRows.push(r);
            if (!settori[r.desc_settore].commesse[r.desc_commessa]) {
                settori[r.desc_settore].commesse[r.desc_commessa] = [];
            }
            settori[r.desc_settore].commesse[r.desc_commessa].push(r);
        });

        // Funzione: dato un array di righe, aggrega per periodo e calcola i KPI
        const aggregateByPeriod = (rows) => {
            const perPeriod = {};
            rows.forEach(r => {
                const key = getPeriodKey(r);
                if (!perPeriod[key]) perPeriod[key] = { costo: 0, ore: 0, ricavo: 0 };
                perPeriod[key].costo += r.tot_costo;
                perPeriod[key].ore += r.tot_ore;
                perPeriod[key].ricavo += r.tot_ricavo;
            });
            // Calcola totale (tutti i periodi insieme)
            const totale = { costo: 0, ore: 0, ricavo: 0 };
            Object.values(perPeriod).forEach(v => {
                totale.costo += v.costo;
                totale.ore += v.ore;
                totale.ricavo += v.ricavo;
            });
            // Per ogni periodo, calcola i 3 KPI
            const kpiPerPeriod = periods.map(p => {
                const d = perPeriod[p] || { costo: 0, ore: 0, ricavo: 0 };
                return {
                    costo_orario: d.ore > 0 ? d.costo / d.ore : null,
                    costo_perc: d.ricavo > 0 ? (d.costo / d.ricavo) * 100 : null,
                    ricavo_orario: d.ore > 0 ? d.ricavo / d.ore : null,
                };
            });
            // KPI per trimestre (solo vista mensile)
            // Somma i valori grezzi dei mesi nel trimestre, poi calcola i rapporti
            const kpiPerTrimestre = {};
            trimGroups.forEach(tg => {
                const trimData = { costo: 0, ore: 0, ricavo: 0 };
                tg.monthIndices.forEach(i => {
                    const d = perPeriod[periods[i]] || { costo: 0, ore: 0, ricavo: 0 };
                    trimData.costo += d.costo;
                    trimData.ore += d.ore;
                    trimData.ricavo += d.ricavo;
                });
                kpiPerTrimestre[tg.key] = {
                    costo_orario: trimData.ore > 0 ? trimData.costo / trimData.ore : null,
                    costo_perc: trimData.ricavo > 0 ? (trimData.costo / trimData.ricavo) * 100 : null,
                    ricavo_orario: trimData.ore > 0 ? trimData.ricavo / trimData.ore : null,
                };
            });
            // KPI per il Totale
            const kpiTotale = {
                costo_orario: totale.ore > 0 ? totale.costo / totale.ore : null,
                costo_perc: totale.ricavo > 0 ? (totale.costo / totale.ricavo) * 100 : null,
                ricavo_orario: totale.ore > 0 ? totale.ricavo / totale.ore : null,
            };
            return { kpiPerPeriod, kpiPerTrimestre, kpiTotale };
        };

        return { periods, periodLabels, anniMap, trimGroups, settori, aggregateByPeriod };
    }, [kpiData, tableViewType]);

    // Calcolo Totale globale (tutte le righe)
    const globalKpi = useMemo(() => {
        if (!tableData.aggregateByPeriod || kpiData.length === 0) return null;
        return tableData.aggregateByPeriod(kpiData);
    }, [kpiData, tableData]);

    // ══ Aggregazione dati grafici per M/T/A ══
    // Ogni useMemo prende i dati mensili dal backend e li raggruppa in base al viewType.
    // Per T e A: sommo i valori additivi (ore, costi, ricavi) e genero una label leggibile.
    // Per il line chart: ricalcolo i rapporti (costo_orario, ricavo_orario) dopo la somma.

    const barAggData = useMemo(() => {
        if (!barData.length) return [];
        const multiYear = new Set(barData.map(d => d.anno)).size > 1;

        if (barViewType === 'mese') {
            return barData.map(d => ({
                ...d,
                label: multiYear
                    ? `${MESI_LABEL[d.mese_num]} ${d.anno}`
                    : MESI_LABEL[d.mese_num],
            }));
        }
        const groups = {};
        barData.forEach(d => {
            const trim = Math.ceil(d.mese_num / 3);
            const key = barViewType === 'trimestre' ? `${d.anno}-${trim}` : `${d.anno}`;
            if (!groups[key]) groups[key] = { anno: d.anno, trim, ore_pei: 0, ore_lavorate: 0 };
            groups[key].ore_pei += d.ore_pei;
            groups[key].ore_lavorate += d.ore_lavorate;
        });
        return Object.entries(groups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, g]) => ({
                ...g,
                label: barViewType === 'trimestre' ? `Trim ${g.trim}\n${g.anno}` : `${g.anno}`,
            }));
    }, [barData, barViewType]);

    const comboAggData = useMemo(() => {
        if (!comboData.length) return [];
        const multiYear = new Set(comboData.map(d => d.anno)).size > 1;

        if (comboViewType === 'mese') {
            return comboData.map(d => ({
                ...d,
                label: multiYear
                    ? `${MESI_LABEL[d.mese_num]} ${d.anno}`
                    : MESI_LABEL[d.mese_num],
            }));
        }
        const groups = {};
        comboData.forEach(d => {
            const key = comboViewType === 'trimestre' ? `${d.anno}-${d.trimestre}` : `${d.anno}`;
            if (!groups[key]) groups[key] = { anno: d.anno, trimestre: d.trimestre, ore_lavorate: 0, costi: 0, ricavi: 0 };
            groups[key].ore_lavorate += d.ore_lavorate;
            groups[key].costi += d.costi;
            groups[key].ricavi += d.ricavi;
        });
        return Object.entries(groups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, g]) => ({
                ...g,
                label: comboViewType === 'trimestre' ? `Trim ${g.trimestre}\n${g.anno}` : `${g.anno}`,
            }));
    }, [comboData, comboViewType]);

    const lineAggData = useMemo(() => {
        if (!lineData.length) return [];
        const multiYear = new Set(lineData.map(d => d.anno)).size > 1;

        if (lineViewType === 'mese') {
            return lineData.map(d => ({
                ...d,
                label: multiYear
                    ? `${MESI_LABEL[d.mese_num]} ${d.anno}`
                    : MESI_LABEL[d.mese_num],
            }));
        }
        // Per T e A: sommo i valori grezzi, poi ricalcolo i rapporti
        const groups = {};
        lineData.forEach(d => {
            const key = lineViewType === 'trimestre' ? `${d.anno}-${d.trimestre}` : `${d.anno}`;
            if (!groups[key]) groups[key] = { anno: d.anno, trimestre: d.trimestre, ore_totali: 0, costi: 0, ricavi: 0 };
            groups[key].ore_totali += d.ore_totali;
            groups[key].costi += d.costi;
            groups[key].ricavi += d.ricavi;
        });
        return Object.entries(groups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, g]) => ({
                ...g,
                label: lineViewType === 'trimestre' ? `Trim ${g.trimestre}\n${g.anno}` : `${g.anno}`,
                costo_orario: g.ore_totali > 0 ? g.costi / g.ore_totali : null,
                ricavo_orario: g.ore_totali > 0 ? g.ricavi / g.ore_totali : null,
            }));
    }, [lineData, lineViewType]);

    // ══ Click handler per cross-filter ══
    // Problema: echarts-for-react non ri-associa gli event handler dopo re-render con notMerge.
    // Soluzione: usiamo refs per i handler e oggetti onEvents stabili (useMemo con deps vuote).
    // Il wrapper stabile delega sempre al ref, che punta all'handler più recente.

    const isSameFilter = (a, b) =>
        a && b && a.type === b.type && a.anno === b.anno &&
        a.mese_num === b.mese_num && a.trimestre === b.trimestre;

    // Refs per handler (sempre aggiornati ad ogni render)
    const barClickRef = useRef(null);
    const comboClickRef = useRef(null);
    const lineClickRef = useRef(null);

    barClickRef.current = (params) => {
        const item = barAggData[params.dataIndex];
        if (!item) return;
        let newFilter;
        if (barViewType === 'mese') {
            newFilter = { type: 'mese', anno: item.anno, mese_num: item.mese_num, source: 'bar', viewType: barViewType };
        } else if (barViewType === 'trimestre') {
            newFilter = { type: 'trimestre', anno: item.anno, trimestre: item.trim, source: 'bar', viewType: barViewType };
        } else {
            newFilter = { type: 'anno', anno: item.anno, source: 'bar', viewType: barViewType };
        }
        setChartFilter(prev => isSameFilter(prev, newFilter) ? null : newFilter);
    };

    comboClickRef.current = (params) => {
        const item = comboAggData[params.dataIndex];
        if (!item) return;
        let newFilter;
        if (comboViewType === 'mese') {
            newFilter = { type: 'mese', anno: item.anno, mese_num: item.mese_num, source: 'combo', viewType: comboViewType };
        } else if (comboViewType === 'trimestre') {
            newFilter = { type: 'trimestre', anno: item.anno, trimestre: item.trimestre, source: 'combo', viewType: comboViewType };
        } else {
            newFilter = { type: 'anno', anno: item.anno, source: 'combo', viewType: comboViewType };
        }
        setChartFilter(prev => isSameFilter(prev, newFilter) ? null : newFilter);
    };

    lineClickRef.current = (params) => {
        const item = lineAggData[params.dataIndex];
        if (!item) return;
        let newFilter;
        if (lineViewType === 'mese') {
            newFilter = { type: 'mese', anno: item.anno, mese_num: item.mese_num, source: 'line', viewType: lineViewType };
        } else if (lineViewType === 'trimestre') {
            newFilter = { type: 'trimestre', anno: item.anno, trimestre: item.trimestre, source: 'line', viewType: lineViewType };
        } else {
            newFilter = { type: 'anno', anno: item.anno, source: 'line', viewType: lineViewType };
        }
        setChartFilter(prev => isSameFilter(prev, newFilter) ? null : newFilter);
    };

    // Oggetti onEvents stabili: creati una sola volta, delegano sempre al ref corrente
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const barEvents = useMemo(() => ({ click: (params) => barClickRef.current(params) }), []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const comboEvents = useMemo(() => ({ click: (params) => comboClickRef.current(params) }), []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const lineEvents = useMemo(() => ({ click: (params) => lineClickRef.current(params) }), []);

    // ══ Opzioni grafici ══

    // Helper: genera itemStyle per una barra con opacity condizionale
    const barItemStyle = (d, baseColor, viewType) => {
        if (!chartFilter) return { color: baseColor };
        const match = filterMatchesItem(chartFilter, d, viewType);
        return { color: baseColor, opacity: match ? 1 : 0.3 };
    };

    // Helper: genera data array per serie line con opacity per punto
    const lineItemData = (aggData, field, baseColor, viewType) => {
        return aggData.map(d => {
            const match = !chartFilter || filterMatchesItem(chartFilter, d, viewType);
            return {
                value: d[field],
                itemStyle: { color: baseColor, opacity: match ? 1 : 0.3 },
            };
        });
    };

    const barOptions = {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        color: ["#16A34A", "#BEF264"],
        legend: { bottom: 0, data: ["Ore PEI", "Ore_Lavorate"] },
        grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
        xAxis: { type: "category", data: barAggData.map(d => d.label) },
        yAxis: { type: "value", name: "Ore PEI e Ore_Lavorate", axisLabel: { formatter: fmtCompact } },
        series: [
            {
                name: "Ore PEI", type: "bar",
                data: barAggData.map(d => ({ value: d.ore_pei, itemStyle: barItemStyle(d, "#16A34A", barViewType) })),
                label: { show: true, position: "top", fontSize: 11, formatter: (p) => fmtCompact(p.value) },
            },
            {
                name: "Ore_Lavorate", type: "bar",
                data: barAggData.map(d => ({ value: d.ore_lavorate, itemStyle: barItemStyle(d, "#BEF264", barViewType) })),
                label: { show: true, position: "top", fontSize: 11, formatter: (p) => fmtCompact(p.value) },
            },
        ],
    };

    const comboOptions = {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, formatter: (params) => { let t = `<strong>${params[0].axisValue}</strong><br/>`; params.forEach(p => { t += `${p.marker} ${p.seriesName}: ${p.seriesName === "Ore (Lavorate)" ? fmtCompact(p.value) : fmtCompactEuro(p.value)}<br/>`; }); return t; } },
        legend: { bottom: 0, data: ["Ore (Lavorate)", "Costi", "Ricavi"] },
        grid: { left: "6%", right: "6%", bottom: "15%", containLabel: true },
        xAxis: { type: "category", data: comboAggData.map(d => d.label), axisLabel: { rotate: 45 } },
        yAxis: [
            { type: "value", name: "Ore (Lavorate)", axisLabel: { formatter: fmtCompact } },
            { type: "value", name: "Costi e Ricavi", axisLabel: { formatter: fmtCompactEuro } },
        ],
        series: [
            {
                name: "Ore (Lavorate)", type: "bar",
                data: comboAggData.map(d => ({ value: d.ore_lavorate, itemStyle: barItemStyle(d, "#93c5fd", comboViewType) })),
            },
            {
                name: "Costi", type: "line", yAxisIndex: 1,
                data: lineItemData(comboAggData, 'costi', "#ef4444", comboViewType),
                smooth: true, lineStyle: { width: 2 }, symbol: "circle", symbolSize: 6,
            },
            {
                name: "Ricavi", type: "line", yAxisIndex: 1,
                data: lineItemData(comboAggData, 'ricavi', "#16A34A", comboViewType),
                smooth: true, lineStyle: { width: 2 }, symbol: "circle", symbolSize: 6,
            },
        ],
    };

    const lineOptions = {
        tooltip: { trigger: "axis", formatter: (params) => { let t = `<strong>${params[0].axisValue}</strong><br/>`; params.forEach(p => { t += `${p.marker} ${p.seriesName}: ${p.seriesName.includes("Ore") ? fmtCompact(p.value) : fmtEuro(p.value)}<br/>`; }); return t; } },
        legend: { bottom: 0, data: ["Costo Orario", "Ricavo Orario", "Ore (Lavorate e NON)"] },
        grid: { left: "6%", right: "6%", bottom: "15%", containLabel: true },
        xAxis: { type: "category", data: lineAggData.map(d => d.label), axisLabel: { rotate: 45 }, boundaryGap: false },
        yAxis: [
            { type: "value", name: "Costi e Ricavi", axisLabel: { formatter: (v) => fmtEuro(v) } },
            { type: "value", name: "Ore (Lavorate e NON)", axisLabel: { formatter: fmtCompact } },
        ],
        series: [
            {
                name: "Costo Orario", type: "line",
                data: lineItemData(lineAggData, 'costo_orario', "#ef4444", lineViewType),
                smooth: true, lineStyle: { width: 2 }, symbol: "circle", symbolSize: 6,
            },
            {
                name: "Ricavo Orario", type: "line",
                data: lineItemData(lineAggData, 'ricavo_orario', "#16A34A", lineViewType),
                smooth: true, lineStyle: { width: 2 }, symbol: "circle", symbolSize: 6,
            },
            {
                name: "Ore (Lavorate e NON)", type: "line", yAxisIndex: 1,
                data: lineItemData(lineAggData, 'ore_totali', "#1e3a5f", lineViewType),
                smooth: true, lineStyle: { width: 2, type: "dashed" }, symbol: "circle", symbolSize: 5,
            },
        ],
    };

    // ── Render helpers per la tabella ──

    // Verifica se una chiave-periodo della tabella corrisponde al filtro attivo.
    // periodKey: "2024-1" (mese), "2024-T1" (trim), "2024" (anno)
    const periodMatchesFilter = (periodKey) => {
        if (!chartFilter) return true;
        const parts = periodKey.split('-');
        const anno = parseInt(parts[0]);
        if (tableViewType === 'mese') {
            // item-like: { anno, mese_num }
            return filterMatchesItem(chartFilter, { anno, mese_num: parseInt(parts[1]) }, 'mese');
        }
        if (tableViewType === 'trimestre') {
            // item-like: { anno, trimestre }
            const trim = parseInt(parts[1].slice(1));
            return filterMatchesItem(chartFilter, { anno, trimestre: trim }, 'trimestre');
        }
        // anno
        return filterMatchesItem(chartFilter, { anno }, 'anno');
    };

    // Verifica se un trimestre della tabella (vista mese) corrisponde al filtro
    const trimMatchesFilter = (trimKey) => {
        if (!chartFilter) return true;
        const parts = trimKey.split('-');
        const anno = parseInt(parts[0]);
        const trim = parseInt(parts[1].slice(1));
        return filterMatchesItem(chartFilter, { anno, trimestre: trim }, 'trimestre');
    };

    const renderKpiCells = (aggResult) => {
        const { kpiPerPeriod, kpiPerTrimestre, kpiTotale } = aggResult;
        const cells = [];

        if (tableViewType === 'mese' && tableData.trimGroups && tableData.trimGroups.length > 0) {
            // Vista mensile: mesi raggruppati per trimestre + TOTALE dopo ogni trim
            tableData.trimGroups.forEach(tg => {
                tg.monthIndices.forEach(i => {
                    if (chartFilter && !periodMatchesFilter(tableData.periods[i])) return;
                    const kpi = kpiPerPeriod[i];
                    cells.push(
                        <td key={`co-${i}`}>{fmtEuro(kpi.costo_orario)}</td>,
                        <td key={`cp-${i}`}>{fmtPerc(kpi.costo_perc)}</td>,
                        <td key={`ro-${i}`}>{fmtEuro(kpi.ricavo_orario)}</td>,
                    );
                });
                // Colonna TOTALE del trimestre (visibile solo se il trim matcha)
                if (!chartFilter || trimMatchesFilter(tg.key)) {
                    const trimKpi = kpiPerTrimestre[tg.key] || { costo_orario: null, costo_perc: null, ricavo_orario: null };
                    cells.push(
                        <td key={`tco-${tg.key}`} className="amb-kpi-td-totale">{fmtEuro(trimKpi.costo_orario)}</td>,
                        <td key={`tcp-${tg.key}`} className="amb-kpi-td-totale">{fmtPerc(trimKpi.costo_perc)}</td>,
                        <td key={`tro-${tg.key}`} className="amb-kpi-td-totale">{fmtEuro(trimKpi.ricavo_orario)}</td>,
                    );
                }
            });
        } else {
            // Vista trimestrale/annuale: celle + totale globale alla fine
            kpiPerPeriod.forEach((kpi, i) => {
                if (chartFilter && !periodMatchesFilter(tableData.periods[i])) return;
                cells.push(
                    <td key={`co-${i}`}>{fmtEuro(kpi.costo_orario)}</td>,
                    <td key={`cp-${i}`}>{fmtPerc(kpi.costo_perc)}</td>,
                    <td key={`ro-${i}`}>{fmtEuro(kpi.ricavo_orario)}</td>,
                );
            });
            cells.push(
                <td key="tot-co" className="amb-kpi-td-totale">{fmtEuro(kpiTotale.costo_orario)}</td>,
                <td key="tot-cp" className="amb-kpi-td-totale">{fmtPerc(kpiTotale.costo_perc)}</td>,
                <td key="tot-ro" className="amb-kpi-td-totale">{fmtEuro(kpiTotale.ricavo_orario)}</td>,
            );
        }

        return cells;
    };

    // ── Multi-level header ──
    // Ogni vista ha i nomi dei periodi e le sub-colonne (C.Orario, C.[%], R.Orario)
    // su righe SEPARATE, come in TrendKPI.
    //
    // Annuale:    2 righe  → anni + sub-colonne
    // Trimestrale: 3 righe → anni + trimestri + sub-colonne
    // Mensile:    4 righe  → ANNO + TRIMESTRE + mesi + sub-colonne

    const renderTableHeaders = () => {
        const { periods, periodLabels, anniMap } = tableData;
        if (!periods || periods.length === 0) return null;

        const subCols = ['C.Orario', 'C.[%]', 'R.Orario'];
        const headerRows = [];
        const anniKeys = Object.keys(anniMap).sort();

        if (tableViewType === 'anno') {
            // ═══ Vista ANNUALE: 2 righe ═══
            const visibleAnni = chartFilter ? anniKeys.filter(a => periodMatchesFilter(a)) : anniKeys;
            const row1 = [<th key="corner" className="amb-kpi-th-corner" rowSpan={2}></th>];
            visibleAnni.forEach(anno => {
                row1.push(<th key={`a-${anno}`} colSpan={3}>{anno}</th>);
            });
            row1.push(<th key="a-tot" colSpan={3} className="amb-kpi-th-totale">Totale</th>);
            headerRows.push(<tr key="h0">{row1}</tr>);

            const row2 = [];
            for (let i = 0; i < visibleAnni.length + 1; i++) {
                const isTotale = i === visibleAnni.length;
                subCols.forEach((sub, si) => {
                    row2.push(<th key={`sub-${i}-${si}`} className={isTotale ? 'amb-kpi-th-totale' : ''}>{sub}</th>);
                });
            }
            headerRows.push(<tr key="h1">{row2}</tr>);

        } else if (tableViewType === 'trimestre') {
            // ═══ Vista TRIMESTRALE: 3 righe ═══
            const row1 = [<th key="corner" className="amb-kpi-th-corner" rowSpan={3}></th>];
            anniKeys.forEach(anno => {
                const visibleInYear = chartFilter ? anniMap[anno].filter(p => periodMatchesFilter(periods[p])) : anniMap[anno];
                if (visibleInYear.length === 0) return;
                row1.push(<th key={`a-${anno}`} colSpan={visibleInYear.length * 3}>{anno}</th>);
            });
            row1.push(<th key="a-tot" colSpan={3} className="amb-kpi-th-totale" rowSpan={2}>Totale</th>);
            headerRows.push(<tr key="h0">{row1}</tr>);

            const row2 = [];
            periods.forEach((p, i) => {
                if (chartFilter && !periodMatchesFilter(p)) return;
                row2.push(<th key={`t-${i}`} colSpan={3}>{periodLabels[i]}</th>);
            });
            headerRows.push(<tr key="h1">{row2}</tr>);

            const row3 = [];
            periods.forEach((p, i) => {
                if (chartFilter && !periodMatchesFilter(p)) return;
                subCols.forEach((sub, si) => {
                    row3.push(<th key={`sub-${i}-${si}`}>{sub}</th>);
                });
            });
            subCols.forEach((sub, si) => {
                row3.push(<th key={`sub-tot-${si}`} className="amb-kpi-th-totale">{sub}</th>);
            });
            headerRows.push(<tr key="h2">{row3}</tr>);

        } else {
            // ═══ Vista MENSILE: 4 righe come TrendKPI ═══
            const { trimGroups: tGroups } = tableData;

            // Helper: mesi visibili per un trimGroup e visibilità del trim
            const getVisibleMonths = (tg) => chartFilter ? tg.monthIndices.filter(idx => periodMatchesFilter(tableData.periods[idx])) : tg.monthIndices;
            const isTrimVisible = (tg) => !chartFilter || getVisibleMonths(tg).length > 0 || trimMatchesFilter(tg.key);
            const showTrimTotale = (tg) => !chartFilter || trimMatchesFilter(tg.key);

            // Row 1: ANNO
            const row1 = [<th key="c1" className="amb-kpi-th-corner">ANNO</th>];
            anniKeys.forEach(anno => {
                const trimsInYear = tGroups.filter(tg => tg.anno === anno);
                let colSpan = 0;
                trimsInYear.forEach(tg => {
                    if (!isTrimVisible(tg)) return;
                    colSpan += getVisibleMonths(tg).length * 3 + (showTrimTotale(tg) ? 3 : 0);
                });
                if (colSpan === 0) return;
                row1.push(<th key={`a-${anno}`} colSpan={colSpan}>{anno}</th>);
            });
            headerRows.push(<tr key="h0">{row1}</tr>);

            // Row 2: TRIMESTRE
            const row2 = [<th key="c2" className="amb-kpi-th-corner">TRIMESTRE</th>];
            tGroups.forEach(tg => {
                if (!isTrimVisible(tg)) return;
                const colSpan = getVisibleMonths(tg).length * 3 + (showTrimTotale(tg) ? 3 : 0);
                row2.push(<th key={`t-${tg.key}`} colSpan={colSpan}>{tg.label}</th>);
            });
            headerRows.push(<tr key="h1">{row2}</tr>);

            // Row 3: Nomi mesi + TOTALE per trim
            const row3 = [<th key="c3" className="amb-kpi-th-corner"></th>];
            tGroups.forEach(tg => {
                if (!isTrimVisible(tg)) return;
                getVisibleMonths(tg).forEach(idx => {
                    row3.push(<th key={`m-${idx}`} colSpan={3}>{periodLabels[idx]}</th>);
                });
                if (showTrimTotale(tg)) {
                    row3.push(<th key={`mt-${tg.key}`} colSpan={3} className="amb-kpi-th-totale">TOTALE</th>);
                }
            });
            headerRows.push(<tr key="h2">{row3}</tr>);

            // Row 4: Sub-colonne
            const row4 = [<th key="c4" className="amb-kpi-th-corner"></th>];
            tGroups.forEach(tg => {
                if (!isTrimVisible(tg)) return;
                getVisibleMonths(tg).forEach(idx => {
                    subCols.forEach((sub, si) => {
                        row4.push(<th key={`s-${idx}-${si}`}>{sub}</th>);
                    });
                });
                if (showTrimTotale(tg)) {
                    subCols.forEach((sub, si) => {
                        row4.push(<th key={`st-${tg.key}-${si}`} className="amb-kpi-th-totale">{sub}</th>);
                    });
                }
            });
            headerRows.push(<tr key="h3">{row4}</tr>);
        }

        return headerRows;
    };

    // ── Render ──

    if (loading) {
        return (
            <Lf23Loader />
        );
    }

    return (
        <div className="amb-gen-container">
            {/* ═══ SEZIONE 1: Tabella KPI – full width ═══ */}
            <div className={`amb-kpi-table-section${isFullscreenTable ? ' fullscreen-trend' : ''}`}>
                <RotateDevice active={isFullscreenTable} />
                <div className="amb-kpi-table-header">
                    <h3 className="trend-kpi-section-title">KPI per Settore</h3>
                    <div className="oper-hr-table-view-type-container">
                        <button type="button" className={`oper-hr-table-view-btn ${tableViewType === 'mese' ? 'active' : ''}`} onClick={() => setTableViewType('mese')} title="Vista Mensile"><span>M</span></button>
                        <button type="button" className={`oper-hr-table-view-btn ${tableViewType === 'trimestre' ? 'active' : ''}`} onClick={() => setTableViewType('trimestre')} title="Vista Trimestrale"><span>T</span></button>
                        <button type="button" className={`oper-hr-table-view-btn ${tableViewType === 'anno' ? 'active' : ''}`} onClick={() => setTableViewType('anno')} title="Vista Annuale"><span>A</span></button>
                        <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenTable(!isFullscreenTable)} title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isFullscreenTable ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="amb-kpi-table-wrapper">
                    <table className="amb-kpi-table">
                        <thead>
                            {renderTableHeaders()}
                        </thead>
                        <tbody>
                            {kpiData.length > 0 && tableData.settori ? (
                                <>
                                    {Object.keys(tableData.settori).sort().map(settore => {
                                        const data = tableData.settori[settore];
                                        const isExpanded = expandedSettori.has(settore);
                                        const settoreAgg = tableData.aggregateByPeriod(data.allRows);
                                        const commesseNames = Object.keys(data.commesse).sort();

                                        return [
                                            // Riga settore (cliccabile)
                                            <tr key={settore} className="amb-kpi-row-settore" onClick={() => toggleSettore(settore)}>
                                                <td className="amb-kpi-cell-label">
                                                    <span className="amb-kpi-toggle">
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={isExpanded ? "expanded" : ""}>
                                                            <polyline points="9 18 15 12 9 6"></polyline>
                                                        </svg>
                                                        {settore}
                                                    </span>
                                                </td>
                                                {renderKpiCells(settoreAgg)}
                                            </tr>,
                                            // Righe commesse (visibili solo se expanded)
                                            ...(isExpanded ? commesseNames.map(comm => {
                                                const commAgg = tableData.aggregateByPeriod(data.commesse[comm]);
                                                return (
                                                    <tr key={`${settore}-${comm}`} className="amb-kpi-row-commessa">
                                                        <td className="amb-kpi-cell-label">{comm}</td>
                                                        {renderKpiCells(commAgg)}
                                                    </tr>
                                                );
                                            }) : []),
                                        ];
                                    })}
                                    {/* Riga Totale globale */}
                                    {globalKpi && (
                                        <tr className="amb-kpi-row-totale">
                                            <td className="amb-kpi-cell-label">Totale</td>
                                            {renderKpiCells(globalKpi)}
                                        </tr>
                                    )}
                                </>
                            ) : (
                                <tr>
                                    <td colSpan={100} className="amb-kpi-cell-empty">Nessun dato disponibile</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ═══ SEZIONE 2: Ore Totali, Costi e Ricavi – full width ═══ */}
            <div className={`amb-gen-section${isFullscreenCombo ? ' fullscreen-trend' : ''}`}>
                <RotateDevice active={isFullscreenCombo} />
                <div className="amb-gen-section-header">
                    <h3 className="amb-gen-section-title">Ore Totali, Costi e Ricavi</h3>
                    <div className="oper-hr-table-view-type-container">
                        <button type="button" className={`oper-hr-table-view-btn ${comboViewType === 'mese' ? 'active' : ''}`} onClick={() => setComboViewType('mese')} title="Vista Mensile"><span>M</span></button>
                        <button type="button" className={`oper-hr-table-view-btn ${comboViewType === 'trimestre' ? 'active' : ''}`} onClick={() => setComboViewType('trimestre')} title="Vista Trimestrale"><span>T</span></button>
                        <button type="button" className={`oper-hr-table-view-btn ${comboViewType === 'anno' ? 'active' : ''}`} onClick={() => setComboViewType('anno')} title="Vista Annuale"><span>A</span></button>
                        <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenCombo(!isFullscreenCombo)} title={isFullscreenCombo ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isFullscreenCombo ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                            </svg>
                        </button>
                    </div>
                </div>
                {comboAggData.length > 0 ? (
                    <ReactECharts key={`combo-${chartKey}`} option={comboOptions} style={{ height: isFullscreenCombo ? "calc(100vh - 80px)" : "350px", width: "100%" }} notMerge={true} onEvents={comboEvents} />
                ) : (
                    <div className="amb-gen-empty">Nessun dato disponibile</div>
                )}
            </div>

            {/* ═══ SEZIONE 3: Ripartizione Ore + Ricavo Costo Orario – affiancati ═══ */}
            <div className="amb-gen-grid">
                {/* Grafico Bar: Ripartizione Ore */}
                <div className={`amb-gen-section${isFullscreenBar ? ' fullscreen-trend' : ''}`}>
                    <RotateDevice active={isFullscreenBar} />
                    <div className="amb-gen-section-header">
                        <h3 className="amb-gen-section-title">Ripartizione Ore e Ore PEI</h3>
                        <div className="oper-hr-table-view-type-container">
                            <button type="button" className={`oper-hr-table-view-btn ${barViewType === 'mese' ? 'active' : ''}`} onClick={() => setBarViewType('mese')} title="Vista Mensile"><span>M</span></button>
                            <button type="button" className={`oper-hr-table-view-btn ${barViewType === 'trimestre' ? 'active' : ''}`} onClick={() => setBarViewType('trimestre')} title="Vista Trimestrale"><span>T</span></button>
                            <button type="button" className={`oper-hr-table-view-btn ${barViewType === 'anno' ? 'active' : ''}`} onClick={() => setBarViewType('anno')} title="Vista Annuale"><span>A</span></button>
                            <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenBar(!isFullscreenBar)} title={isFullscreenBar ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenBar ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                                </svg>
                            </button>
                        </div>
                    </div>
                    {barAggData.length > 0 ? (
                        <ReactECharts key={`bar-${chartKey}`} option={barOptions} style={{ height: isFullscreenBar ? "calc(100vh - 80px)" : "350px", width: "100%" }} notMerge={true} onEvents={barEvents} />
                    ) : (
                        <div className="amb-gen-empty">Nessun dato disponibile</div>
                    )}
                </div>

                {/* Grafico Line: Ricavo e Costo Orario */}
                <div className={`amb-gen-section${isFullscreenLine ? ' fullscreen-trend' : ''}`}>
                    <RotateDevice active={isFullscreenLine} />
                    <div className="amb-gen-section-header">
                        <h3 className="amb-gen-section-title">Ricavo e Costo Orario</h3>
                        <div className="oper-hr-table-view-type-container">
                            <button type="button" className={`oper-hr-table-view-btn ${lineViewType === 'mese' ? 'active' : ''}`} onClick={() => setLineViewType('mese')} title="Vista Mensile"><span>M</span></button>
                            <button type="button" className={`oper-hr-table-view-btn ${lineViewType === 'trimestre' ? 'active' : ''}`} onClick={() => setLineViewType('trimestre')} title="Vista Trimestrale"><span>T</span></button>
                            <button type="button" className={`oper-hr-table-view-btn ${lineViewType === 'anno' ? 'active' : ''}`} onClick={() => setLineViewType('anno')} title="Vista Annuale"><span>A</span></button>
                            <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenLine(!isFullscreenLine)} title={isFullscreenLine ? 'Esci da tutto schermo' : 'Tutto schermo'}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenLine ? (<><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" y1="10" x2="21" y2="3"></line><line x1="3" y1="21" x2="10" y2="14"></line></>) : (<><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></>)}
                                </svg>
                            </button>
                        </div>
                    </div>
                    {lineAggData.length > 0 ? (
                        <ReactECharts key={`line-${chartKey}`} option={lineOptions} style={{ height: isFullscreenLine ? "calc(100vh - 80px)" : "350px", width: "100%" }} notMerge={true} onEvents={lineEvents} />
                    ) : (
                        <div className="amb-gen-empty">Nessun dato disponibile</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Generale;
